import { z } from 'zod';
import {
  changePasswordRequestSchema,
  createHouseholdRequestSchema,
  emailSchema,
  passwordSchema,
  registerRequestSchema,
  resetPasswordRequestSchema,
} from '@fewerbags/schema';

/** Field-level error map derived from a zod result. */
export type FieldErrors<K extends string = string> = Partial<Record<K, string>>;

export function fieldErrors<K extends string>(error: z.ZodError): FieldErrors<K> {
  const out: FieldErrors<K> = {};
  for (const issue of error.issues) {
    const key = String(issue.path[0] ?? 'form') as K;
    out[key] ??= issue.message;
  }
  return out;
}

/** Read a form into a plain object (empty strings become undefined for optional fields). */
export function formValues(form: HTMLFormElement): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of new FormData(form)) {
    if (typeof v === 'string') out[k] = v;
  }
  return out;
}

export function parseForm<S extends z.ZodType>(
  schema: S,
  form: HTMLFormElement,
): { data: z.output<S>; errors?: undefined } | { data?: undefined; errors: FieldErrors } {
  const r = schema.safeParse(formValues(form));
  return r.success ? { data: r.data } : { errors: fieldErrors(r.error) };
}

export const loginFormSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Enter your password').max(200),
});

export const registerFormSchema = registerRequestSchema
  .omit({ householdName: true, inviteCode: true })
  .extend({
    mode: z.enum(['create', 'join']),
    householdName: z.string().trim().max(120).optional(),
    inviteCode: z.string().trim().max(64).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.mode === 'create' && !v.householdName) {
      ctx.addIssue({ code: 'custom', path: ['householdName'], message: 'Name your household' });
    }
    if (v.mode === 'join' && (!v.inviteCode || v.inviteCode.length < 4)) {
      ctx.addIssue({ code: 'custom', path: ['inviteCode'], message: 'Enter your invite code' });
    }
  })
  .transform((v) => ({
    email: v.email,
    password: v.password,
    displayName: v.displayName,
    ...(v.mode === 'create' ? { householdName: v.householdName } : { inviteCode: v.inviteCode }),
  }));

export const forgotPasswordFormSchema = z.object({ email: emailSchema });

export const resetPasswordFormSchema = resetPasswordRequestSchema
  .extend({ confirm: z.string() })
  .refine((v) => v.password === v.confirm, { path: ['confirm'], message: 'Passwords don’t match' });

export const changePasswordFormSchema = changePasswordRequestSchema
  .extend({ confirm: z.string() })
  .refine((v) => v.newPassword === v.confirm, {
    path: ['confirm'],
    message: 'Passwords don’t match',
  });

export const createHouseholdFormSchema = createHouseholdRequestSchema.extend({
  defaultServings: z.coerce.number().int().min(1).max(50).optional(),
});

export const joinHouseholdFormSchema = z.object({
  code: z.string().trim().min(4, 'Enter your invite code').max(64),
});

export const passwordRules = passwordSchema;
